/**
 * 악덕알리미 앱(BD) 연동 흐름 — $j.F.Devil.App
 *   syncSettings()    알림 방식·테마·회원번호를 앱에 동기화
 *   registerDevice()  기기정보(토큰·버전·기기명·환경 스냅샷)를 앱에 물어 서버에 저장
 *   setMasterPref()   악덕마스터 단위 토글을 앱 로컬에 즉시 반영
 *
 * 앱과 주고받는 통로는 공용 브리지($j.F.App)다. 브리지는 액션을 보내고 받기만 하고,
 * 악덕 화면이 **무엇을 언제** 앱에 보내는지는 여기서 정한다. 장비불러 흐름은 이 파일에 두지 않는다.
 * 앱 외 환경(브라우저)·대리진입 중이면 브리지가 알아서 no-op 이다.
 */
import Util from '../../Common/Util';

export default class App {
    constructor() {
        this._registered = false; // registerDevice() 가 이미 돌았는가 (중복 실행 방지)
    }

    /**
     * 네이티브 동기화 단일 진입점 — push.alertMode + theme + member.mbno.
     * 설정값은 서버에서 MEMBER.app_config 에 이미 실려오므로(getMemberJs) 추가 ajax 없음.
     * 모든 /b 페이지에서 일관 동작하도록 Sites/Devil/Bootstrap 이 진입 시 1회 호출한다.
     */
    syncSettings() {
        // 앱 밖(폰 브라우저)에서는 보내지 않는다 — registerDevice()·setMasterPref() 와 같은 가드.
        //   /b/d/{토큰}(업체 소명 페이지)은 비회원이 **문자 링크를 폰 브라우저로** 여는 유일한 /b 페이지라,
        //   가드가 없으면 runAppBroker 가 "안드로이드앱에서 실행해주세요." alert 를 띄운다(2026-10-06 상필 님 QA).
        if (!$j.F.App.isApp()) return;

        const cfg = (typeof MEMBER !== 'undefined' && MEMBER) ? (MEMBER.app_config || {}) : {};
        const settings = { push: { alertMode: cfg.push || 'sound' } };
        // 화면 테마(light|dark) — <html data-theme> 현재값(서버 렌더 + 토글 반영). 네이티브 상태바/스플래시 테마 동기화용.
        settings.theme = document.documentElement.getAttribute('data-theme') || 'light';
        const mbno = (typeof MEMBER !== 'undefined' && MEMBER) ? Util.int(MEMBER.num) : 0;
        // phone·phoneCheck — 앱이 회원 번호와 폰 SIM 번호를 맞춰 보고, 다르면 로그아웃시키고 통화기록을 안 올린다.
        //   phoneCheck=0 은 개발/QA 서버·앱마켓 심사계정 (MemberQueryService::getMemberJs). 옛 앱은 두 필드를 무시한다.
        if (mbno) settings.member = {
            mbno,
            phone: String(MEMBER.mem_phone || '').replace(/[^0-9]/g, ''),
            phoneCheck: Util.int(MEMBER.dvPhoneCheck),
        };
        $j.F.App.syncSettings(settings);
    }

    /**
     * 기기정보 등록 — 앱에 기기정보를 물어, 회신(토큰·앱버전·기기명·(실렸으면) 앱 환경 스냅샷 diag)을
     * POST /b/ajax/member/device 로 올린다. 장비불러 $j.F.JbApp.start() 에서 GPS·지도·폼·페이지코드·
     * 헬스체크를 뺀 것이다 — 악덕알리미는 위치 권한이 없고 앱이 페이지코드를 쓰지 않는다.
     *
     * 반드시 syncSettings() 뒤에 부른다 — 앱은 회원번호를 받은 뒤에야 스냅샷을 만든다.
     * 앱 외 환경·대리진입이면 no-op. getDeviceContext 를 모르는 구앱은 회신이 오지 않는다.
     */
    registerDevice() {
        if (this._registered) return;
        this._registered = true;

        if (!$j.F.App.isApp()) return;

        $j.Common.EventBus.on('$j.F.Devil.App', 'app:device:context', (e, data) => {
            this._saveDevice(data);
        });
        $j.F.App.getDeviceContext();
    }

    /**
     * 기기정보 회신 저장. 비로그인이면 저장하지 않는다 — 회원번호는 서버가 세션에서 얻으므로 보내지 않는다.
     *
     * 페이지마다 부르므로 모바일웹과 같은 규칙으로 줄인다: 마지막 저장 후 5분이 안 지났고 토큰이 같으면 건너뛴다.
     * 스냅샷이 실린 회신은 무조건 보낸다 — 앱은 하루 한 번 싣는데 그 순간이 스로틀에 걸리면 그날 치가 버려진다.
     * 경계쿠키(CK_GPS_TIME·CK_GG_TOKEN)는 모바일웹과 같은 키다 — 앱마다 웹뷰 쿠키 저장소가 따로라 섞이지 않는다.
     *
     * @param {Object} data callbackDeviceContext 페이로드 {token, mobie_ver, modelName, diag?}
     */
    _saveDevice(data) {
        const mbNo = (typeof MEMBER !== 'undefined' && MEMBER && MEMBER.num) ? MEMBER.num : 0;
        if (!mbNo) return;   // 비로그인 — 저장할 대상이 없다

        let mobieVer = parseFloat(data.mobie_ver);
        if (isNaN(mobieVer)) mobieVer = 1;
        // 문자열로 넘긴다 — 폼 직렬화가 숫자·불리언을 문자열로 바꿔 원본이 뭉개지기 때문 (모바일웹과 같은 이유)
        const diag = !data.diag ? ''
                   : (typeof data.diag === 'string' ? data.diag : JSON.stringify(data.diag));

        const cookie = $j.Common.Store.cookie;
        const last = cookie.get(G5['CK_GPS_TIME']);
        const elapsed = dayjs().diff(dayjs(last), 'second');
        const due = !elapsed || elapsed > 300
                 || cookie.get(G5['CK_GG_TOKEN']) != data.token
                 || !!diag;
        if (!due) return;

        $.ajax({
            type: 'post',
            url: '/b/ajax/member/device',
            data: {
                'uver':      mobieVer,
                'token':     data.token,
                'modelname': (data.modelName === undefined) ? '' : data.modelName,
                'diag':      diag,
                // 실행중인 앱 — 패키지를 모르는 구앱은 빈값 → 서버가 쿠키(G5_DEVIL 인증이 BD 로 둔다)로 폴백
                'app':       $j.F.App.hasPackage() ? $j.F.App.getPackage() : '',
                // 플랫폼 — iOS 앱만 'ios' 를 싣는다. 안 오면 서버가 UA 로 판정한다 (g5_member_devices.mobile_type)
                'platform':  data.platform || '',
            },
        });

        cookie.set(G5['CK_GPS_TIME'], dayjs().format('YYYY-MM-DD HH:mm:ss'));
        cookie.set(G5['CK_GG_TOKEN'], data.token);
    }

    /**
     * 악덕마스터 단위 토글을 앱 로컬(Room)에 즉시 동기화 — 브로커 액션 setMasterPref.
     * 한 호출에 detect|share 중 정확히 하나만 (동시 전송 금지).
     *   detect : 체불번호감지(경고팝업) on/off (= 서버 allowed 와 동일 극성)
     *   share  : 통화이력공유 on/off
     * 서버 영속화는 기존 REST(API 3·6)가 담당하고, 본 호출은 단말 즉시반영용(fire-and-forget).
     * 앱 외(브라우저)·미구현 앱이면 조용히 무시한다. (20260527_BADCALL_APP_BROKER_ACTIONS.md §7)
     * @param {{masterNum:number, detect?:boolean, share?:boolean}} pref
     * @returns {boolean}
     */
    setMasterPref(pref = {}) {
        if (!pref || !pref.masterNum) return false;
        if (!$j.F.App.isApp()) return false; // 앱 외 환경은 조용히 무시 (alert 방지)
        return $j.F.App.runAppBroker('setMasterPref', pref);
    }
}
